import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { commentService } from '../../../../services/comment.service';
import { selectAuth } from '../../../../redux/slices/auth';
import { PERMISSIONS } from '../../../../shared/permission';
import ConfirmModal from '../../../UI/ConfirmModal/ConfirmModal';
import styles from './IdeaComments.module.css';

const MAX_LENGTH = 2000;

function buildTree(flat) {
    const map = {};
    const roots = [];
    flat
        .filter(c => c.status !== 'hidden')
        .forEach(c => { map[c.id] = { ...c, children: [], parentAuthor: null }; });
    Object.values(map).forEach(c => {
        if (c.parent_comment_id != null && map[c.parent_comment_id]) {
            const parent = map[c.parent_comment_id];
            c.parentAuthor = parent.author;
            parent.children.push(c);
        } else {
            roots.push(c);
        }
    });
    return roots;
}

function formatName(author) {
    if (!author) return '';
    return [author.last_name, author.first_name, author.middle_name].filter(Boolean).join(' ');
}

function getInitials(author) {
    if (!author) return '?';
    return [author.first_name, author.last_name]
        .filter(Boolean)
        .map(n => n[0])
        .join('')
        .toUpperCase();
}

function formatTime(dateStr) {
    return new Date(dateStr).toLocaleString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function CommentItem({
    comment,
    currentUser,
    isAdmin,
    onReply,
    onEdit,
    onDelete,
    replyingTo,
    editingId,
    replyText,
    setReplyText,
    editText,
    setEditText,
    onSubmitReply,
    onSaveEdit,
    onCancelReply,
    onCancelEdit,
    submitting,
    depth,
}) {
    const { t } = useTranslation('common');

    const isDeleted = comment.status === 'deleted';
    const isEditing = editingId === comment.id;
    const isReplying = replyingTo === comment.id;
    const isAuthor = comment.author?.id === currentUser?.id;

    // Show "↳ Name" only for replies to replies (not direct replies to root)
    const showReplyTo =
        comment.parent_comment_id != null &&
        comment.root_comment_id != null &&
        comment.parent_comment_id !== comment.root_comment_id &&
        comment.parentAuthor != null;

    return (
        <div className={styles.commentWrapper}>
            <div className={`${styles.commentCard} ${depth > 0 ? styles.nestedCard : ''}`}>
                {isDeleted ? (
                    <div className={styles.deletedStub}>{t('comments.deleted')}</div>
                ) : (
                    <>
                        {showReplyTo && (
                            <div className={styles.replyToLabel}>
                                ↳ <span className={styles.replyToName}>{formatName(comment.parentAuthor)}</span>
                            </div>
                        )}

                        <div className={styles.commentHeader}>
                            <div className={styles.avatar}>{getInitials(comment.author)}</div>
                            <div className={styles.headerMeta}>
                                <span className={styles.authorName}>{formatName(comment.author)}</span>
                                <div className={styles.headerSub}>
                                    <span className={styles.time}>{formatTime(comment.created_at)}</span>
                                    {comment.edited_at && (
                                        <span className={styles.editedBadge}>· {t('comments.edited')}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {isEditing ? (
                            <div className={styles.editForm}>
                                <textarea
                                    className={styles.textarea}
                                    value={editText}
                                    onChange={e => setEditText(e.target.value)}
                                    maxLength={MAX_LENGTH}
                                    rows={3}
                                />
                                <div className={styles.formFooter}>
                                    <span className={styles.charCount}>{editText.length}/{MAX_LENGTH}</span>
                                    <div className={styles.formButtons}>
                                        <button className={styles.cancelBtn} onClick={onCancelEdit} type="button">
                                            {t('comments.cancel')}
                                        </button>
                                        <button
                                            className={styles.submitBtn}
                                            onClick={onSaveEdit}
                                            type="button"
                                            disabled={submitting || !editText.trim()}
                                        >
                                            {t('comments.save')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className={styles.body}>{comment.body}</p>
                        )}

                        {!isEditing && (
                            <div className={styles.actions}>
                                <div className={styles.actionsLeft}>
                                    {(isAuthor || isAdmin) && (
                                        <button
                                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                            onClick={() => onDelete(comment.id)}
                                            type="button"
                                        >
                                            {t('comments.delete')}
                                        </button>
                                    )}
                                </div>
                                <div className={styles.actionsRight}>
                                    {isAuthor && (
                                        <button className={styles.actionBtn} onClick={() => onEdit(comment)} type="button">
                                            {t('comments.edit')}
                                        </button>
                                    )}
                                    <button className={styles.actionBtn} onClick={() => onReply(comment.id)} type="button">
                                        {t('comments.reply')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {isReplying && (
                <div className={`${styles.replyFormCard} ${depth > 0 ? styles.nestedCard : ''}`}>
                    <textarea
                        className={styles.textarea}
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        placeholder={t('comments.reply_placeholder')}
                        maxLength={MAX_LENGTH}
                        rows={3}
                        autoFocus
                    />
                    <div className={styles.formFooter}>
                        <span className={styles.charCount}>{replyText.length}/{MAX_LENGTH}</span>
                        <div className={styles.formButtons}>
                            <button className={styles.cancelBtn} onClick={onCancelReply} type="button">
                                {t('comments.cancel')}
                            </button>
                            <button
                                className={styles.submitBtn}
                                onClick={onSubmitReply}
                                type="button"
                                disabled={submitting || !replyText.trim()}
                            >
                                {t('comments.send')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {comment.children?.length > 0 && (
                <div className={styles.children}>
                    {comment.children.map(child => (
                        <CommentItem
                            key={child.id}
                            comment={child}
                            currentUser={currentUser}
                            isAdmin={isAdmin}
                            onReply={onReply}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            replyingTo={replyingTo}
                            editingId={editingId}
                            replyText={replyText}
                            setReplyText={setReplyText}
                            editText={editText}
                            setEditText={setEditText}
                            onSubmitReply={onSubmitReply}
                            onSaveEdit={onSaveEdit}
                            onCancelReply={onCancelReply}
                            onCancelEdit={onCancelEdit}
                            submitting={submitting}
                            depth={Math.min(depth + 1, 1)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function IdeaComments({ ideaId }) {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');
    const { user } = useSelector(selectAuth);

    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newText, setNewText] = useState('');
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    const isAdmin = user?.permissions?.some(p =>
        p === PERMISSIONS.USERS.EDIT || p === PERMISSIONS.USERS.CREATE
    );

    const fetchComments = useCallback(async () => {
        try {
            const data = await commentService.getComments(ideaId);
            setComments(data);
        } catch (err) {
            console.error('Failed to fetch comments', err);
        } finally {
            setIsLoading(false);
        }
    }, [ideaId]);

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    const handleErrorToast = (err) => {
        const errorKey = err?.payload?.error;
        toast.error(tValidation(typeof errorKey === 'string' ? errorKey : 'something_went_wrong'));
    };

    const handleSubmitNew = async () => {
        if (!newText.trim() || submitting) return;
        setSubmitting(true);
        try {
            await commentService.createComment(ideaId, { text: newText.trim(), parentCommentId: null });
            toast.success(tValidation('comment_created_successfully'));
            setNewText('');
            fetchComments();
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReply = (commentId) => {
        setReplyingTo(commentId);
        setReplyText('');
        setEditingId(null);
        setEditText('');
    };

    const handleSubmitReply = async () => {
        if (!replyText.trim() || submitting) return;
        setSubmitting(true);
        try {
            await commentService.createComment(ideaId, { text: replyText.trim(), parentCommentId: replyingTo });
            toast.success(tValidation('comment_created_successfully'));
            setReplyingTo(null);
            setReplyText('');
            fetchComments();
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (comment) => {
        setEditingId(comment.id);
        setEditText(comment.body);
        setReplyingTo(null);
        setReplyText('');
    };

    const handleSaveEdit = async () => {
        if (!editText.trim() || submitting) return;
        setSubmitting(true);
        try {
            await commentService.editComment(editingId, { text: editText.trim() });
            toast.success(tValidation('comment_updated_successfully'));
            setComments(prev =>
                prev.map(c =>
                    c.id === editingId
                        ? { ...c, body: editText.trim(), edited_at: new Date().toISOString() }
                        : c
                )
            );
            setEditingId(null);
            setEditText('');
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (commentId) => {
        setDeleteTargetId(commentId);
    };

    const handleConfirmDelete = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await commentService.deleteComment(deleteTargetId);
            toast.success(tValidation('comment_deleted_successfully'));
            setComments(prev =>
                prev.map(c =>
                    c.id === deleteTargetId
                        ? { ...c, status: 'deleted', body: null, author: null }
                        : c
                )
            );
            setDeleteTargetId(null);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setSubmitting(false);
        }
    };

    const tree = buildTree(comments);
    const activeCount = comments.filter(c => c.status === 'active').length;

    return (
        <>
        <div className={styles.container}>
            <h2 className={styles.sectionTitle}>
                {t('comments.title')}
                {!isLoading && <span className={styles.count}>{activeCount}</span>}
            </h2>

            <div className={styles.newCommentForm}>
                <textarea
                    className={styles.textarea}
                    value={newText}
                    onChange={e => setNewText(e.target.value)}
                    placeholder={t('comments.placeholder')}
                    maxLength={MAX_LENGTH}
                    rows={3}
                />
                <div className={styles.formFooter}>
                    <span className={styles.charCount}>{newText.length}/{MAX_LENGTH}</span>
                    <button
                        className={styles.submitBtn}
                        onClick={handleSubmitNew}
                        type="button"
                        disabled={submitting || !newText.trim()}
                    >
                        {t('comments.send')}
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className={styles.empty}>{t('loading', 'Loading...')}</div>
            ) : tree.length === 0 ? (
                <div className={styles.empty}>{t('comments.no_comments')}</div>
            ) : (
                <div className={styles.commentList}>
                    {tree.map(comment => (
                        <CommentItem
                            key={comment.id}
                            comment={comment}
                            currentUser={user}
                            isAdmin={isAdmin}
                            onReply={handleReply}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            replyingTo={replyingTo}
                            editingId={editingId}
                            replyText={replyText}
                            setReplyText={setReplyText}
                            editText={editText}
                            setEditText={setEditText}
                            onSubmitReply={handleSubmitReply}
                            onSaveEdit={handleSaveEdit}
                            onCancelReply={() => { setReplyingTo(null); setReplyText(''); }}
                            onCancelEdit={() => { setEditingId(null); setEditText(''); }}
                            submitting={submitting}
                            depth={0}
                        />
                    ))}
                </div>
            )}
        </div>

        <ConfirmModal
            isOpen={deleteTargetId !== null}
            onClose={() => setDeleteTargetId(null)}
            onConfirm={handleConfirmDelete}
            title={t('comments.delete_confirm_title')}
            message={t('comments.delete_confirm_message')}
            confirmText={t('comments.delete')}
            cancelText={t('comments.cancel')}
            isLoading={submitting}
        />
        </>
    );
}

export default IdeaComments;
